import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { Text } from "@/components/ui/baseUi/text"
import { BoxBanking } from "@/modules/box/components/boxBanking/boxBanking"
import { BoxSettingsWrapper } from "@/modules/box/components/boxSettingsForm/boxSettingsWrapper"
import { BoxForm } from "@/modules/box/components/boxUpdateForm/boxForm"
import { cn } from "@/modules/lib/utils"
import errorNoRepLight from "@images/error/errorNoRepLight.svg"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxStaffPermissions, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { ErrorCard } from "@modules/ui/components/error/errorCard"
import { ScreenHeader } from "@modules/ui/components/header"
import { LoadingPage } from "@modules/ui/components/loadingPage"
import { useQueryParam } from "@modules/utils/navigation/useQueryParam"
import { startTransition, useEffect } from "react"
import { ErrorBoundary } from "react-error-boundary"

enum Tab {
  Box = "box",
  Settings = "settings",
  Banking = "banking",
}

export function BoxSettingsPage() {
  const [tab, setTab] = useQueryParam("tab")
  const boxNavigate = useBoxNavigate()
  const { result: box, isLoading, error } = useCurrentBox()
  const canSeeGymInformation = useBoxStaffPermission(BoxStaffPermissions.SEE_GYM_INFORMATION)
  const canSeeBankDetails = useBoxStaffPermission(BoxStaffPermissions.SEE_BANK_DETAILS)

  const tabModel: { label: string; tab: Tab; command: () => void }[] = [
    { label: word("box.settings.page.tabs.box"), command: () => setTab(Tab.Box), tab: Tab.Box },
    { label: word("box.settings.page.tabs.settings"), command: () => setTab(Tab.Settings), tab: Tab.Settings },
    { label: word("box.settings.page.tabs.banking"), command: () => setTab(Tab.Banking), tab: Tab.Banking },
  ].filter((tab) => {
    if (tab.tab === Tab.Banking && !canSeeBankDetails) return false
    return true
  })

  const selectedTab = (tab as Tab) ?? Tab.Box
  function handleTabChange(value: Tab) {
    startTransition(() => {
      setTab(value)
    })
  }

  useEffect(() => {
    if (isLoading) return
    // PERMISSIONS - Admin page requires SEE_GYM_INFORMATION
    if (!canSeeGymInformation && box?.currentUserRole === BoxStaffRole.FINANCIAL) {
      boxNavigate(Path.Box.Finances.Root)
      return
    }
    if (!canSeeGymInformation) {
      boxNavigate(Path.Box.Planning)
      return
    }

    if (selectedTab === Tab.Banking && !canSeeBankDetails) {
      setTab(Tab.Box)
    }
  }, [canSeeGymInformation, canSeeBankDetails, selectedTab, box, isLoading])

  return (
    <div className="w-full p-6">
      <ScreenHeader title={word("box.settings.page.title")} subTitle={word("box.settings.page.subtitle")} />
      <div className="flex flex-1 flex-col gap-6">
        <ErrorBoundary fallback={<BoxSettingsErrorView />}>
          {box ? (
            <Tabs defaultValue={selectedTab} className="relative flex w-full flex-col">
              <TabsList variant="default" className="w-full border-neutral-100">
                {tabModel.map((tab) => (
                  <TabsTrigger key={tab.tab} variant="default" value={tab.tab} onClick={() => handleTabChange(tab.tab)}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value={Tab.Box}>
                <BoxForm box={box} className="mt-6" />
              </TabsContent>
              <TabsContent value={Tab.Settings}>
                <BoxSettingsWrapper className="mt-6" />
              </TabsContent>
              <TabsContent value={Tab.Banking}>
                <BoxBanking box={box} className="mt-6" />
              </TabsContent>
            </Tabs>
          ) : isLoading ? (
            <LoadingPage />
          ) : error ? (
            <ErrorCard />
          ) : null}
        </ErrorBoundary>
      </div>
    </div>
  )
}

function BoxSettingsErrorView({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div className={cn("flex h-80 min-h-full w-full flex-col items-center justify-center gap-2", className)} {...props}>
      <Text fontStyle="BM" className="text-textDefault-body">
        {word("global.error.norep")}
      </Text>
      <img src={errorNoRepLight} />
      <Text fontStyle="BM" className="text-textDefault-caption">
        {word("global.error.somethingGoneWrong")}
      </Text>
    </div>
  )
}
